import { Button } from '@repo/studio-design-system-ui-button'
import type { ButtonProps } from '@repo/studio-design-system-ui-button'
import { LFS_URL } from '@repo/studio-utils/config/env'
import { AnimatePresence, m } from 'framer-motion'
import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { tw } from 'utils/classnames'
import { Label } from '../../label'
import { SuccessIndicator } from '../../success-indicator'

const HIGHLIGHT_BG = `${LFS_URL}/studio/coins/highlighted-card-bg.webp`
export type PurchaseCardVariant = 'default' | 'highlighted'

function Root({
  children,
  variant = 'default',
  className,
  ref,
}: {
  children: React.ReactNode
  variant?: PurchaseCardVariant
  className?: string
  ref?: React.Ref<HTMLDivElement>
}) {
  return (
    <div
      ref={ref}
      className={tw(
        'relative flex flex-col rounded-3 p-3 md:p-5 lg:p-5 overflow-clip group',
        variant === 'default' && 'bg-gray-1000 border border-gray-700-opacity hover:bg-gray-900',
        className,
      )}
    >
      <img
        alt=""
        className={tw(
          'absolute inset-0 size-full scale-150 -translate-y-[25%] group-hover:translate-y-[0] transition-transform duration-1000',
          variant !== 'highlighted' && 'no-display',
        )}
        src={HIGHLIGHT_BG}
      />
      <div className="flex flex-1 flex-col z-[1]">{children}</div>
    </div>
  )
}

function Badge({ children }: { children: React.ReactNode }) {
  return <Label className="w-fit">{children}</Label>
}

function Title({
  children,
  variant = 'default',
}: {
  children: React.ReactNode
  variant?: PurchaseCardVariant
}) {
  const t = useStudioTranslate()
  return (
    <div className="flex justify-between items-center mb-5">
      <h3
        className={tw(
          'typography-14 md:typography-18 lg:typography-20 line-clamp-1',
          variant === 'highlighted' && 'text-service-white-white',
        )}
      >
        {children}
      </h3>

      {variant === 'highlighted' ? (
        <PurchaseCard.Badge>
          {t('studio.coins.top_up_dialog.subscriptions.best_value')}
        </PurchaseCard.Badge>
      ) : null}
    </div>
  )
}

function Price({
  price,
  suffix,
  variant = 'default',
}: {
  price: string
  suffix?: string
  variant?: PurchaseCardVariant
}) {
  return (
    <p className="flex items-baseline gap-1">
      <span
        className={tw(
          'typography-18 md:typography-20 lg:typography-24',
          variant === 'highlighted' && 'text-service-white-white',
        )}
      >
        {price}
      </span>
      {suffix ? (
        <span
          className={tw(
            'typography-10 md:typography-12 lg:typography-13',
            variant === 'highlighted' ? 'text-service-white-white' : 'text-gray-400',
          )}
        >
          {suffix}
        </span>
      ) : null}
    </p>
  )
}

function UnitPrice({
  children,
  variant = 'default',
}: {
  children: React.ReactNode
  variant?: PurchaseCardVariant
}) {
  return (
    <p
      className={tw(
        'typography-10 md:typography-12 lg:typography-13 mb-2',
        variant === 'highlighted' ? 'text-service-white-white' : 'text-gray-400',
      )}
    >
      {children}
    </p>
  )
}

function BuyButton({
  children,
  variant = 'default',
  success = false,
  isHighestValue = false,
  ...props
}: {
  variant?: PurchaseCardVariant
  success?: boolean
  isHighestValue?: boolean
} & Omit<ButtonProps, 'variant' | 'size' | 'className'>) {
  return (
    <>
      <BuyButtonInner
        className="no-display lg:flex"
        size="md"
        variant={variant}
        success={success}
        isHighestValue={isHighestValue}
        {...props}
      >
        {children}
      </BuyButtonInner>
      <BuyButtonInner
        className="lg:no-display"
        size="sm"
        variant={variant}
        success={success}
        isHighestValue={isHighestValue}
        {...props}
      >
        {children}
      </BuyButtonInner>
    </>
  )
}

// backdrop-filter:none or we run into trouble with after:absolute
const BUYBUTTON_CLASS =
  "static mt-auto w-full [backdrop-filter:none] after:absolute after:inset-0 after:content-['']"

function BuyButtonInner({
  className,
  children,
  variant,
  success,
  loading,
  isHighestValue,
  size,
  ...props
}: {
  variant?: PurchaseCardVariant
  success?: boolean
  isHighestValue?: boolean
} & Omit<ButtonProps, 'variant'>) {
  function resolveButtonVariant(): 'overlay' | 'primary' | 'secondary' {
    if (variant === 'highlighted') {
      return 'overlay'
    }
    if (isHighestValue) {
      return 'primary'
    }
    return 'secondary'
  }

  const resolvedVariant = resolveButtonVariant()

  return (
    <Button
      variant={resolvedVariant}
      size={size}
      {...(variant === 'highlighted' ? { 'data-studio-theme': 'dark' } : {})}
      loading={loading}
      {...props}
      className={tw(BUYBUTTON_CLASS, loading && 'relative', className)}
    >
      <AnimatePresence mode="wait" initial={false}>
        {success ? (
          <SuccessIndicator label="Success" />
        ) : (
          <m.span
            key="default"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.3 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
          >
            {children}
          </m.span>
        )}
      </AnimatePresence>
    </Button>
  )
}

export const PurchaseCard = {
  Root,
  Badge,
  Title,
  Price,
  UnitPrice,
  BuyButton,
}
